import {ComponentOverrideFlag} from '../../../utils/flags/flags';
import type {ConnectElementConfig} from '../ConnectElementList';

export const debugParameter = 'debug' as const;
export const privacyPolicyUrlParameter = 'privacy-policy-url' as const;
export const howCapitalWorksUrlParameter = 'how-capital-works-url' as const;
export const capitalSchemeParameter = 'capital-scheme' as const;
export const defaultFinancingOfferParameter =
  'default-financing-offer' as const;
export const resumeInitiatedApplicationParameter =
  'resume-initiated-application' as const;

export const CapitalFinancingApplicationConfig: ConnectElementConfig = {
  Component: () =>
    import('../../../components/Capital/CapitalFinancingApplication'),
  enabledOverrideFlag: ComponentOverrideFlag.capital_financing_application,
  htmlAttributes: {
    [debugParameter]: {
      isInternal: true,
    },
    [privacyPolicyUrlParameter]: {
      isInternal: false,
    },
    [howCapitalWorksUrlParameter]: {
      isInternal: false,
    },
    [defaultFinancingOfferParameter]: {
      isInternal: false,
    },
    [resumeInitiatedApplicationParameter]: {
      isInternal: false,
    },
    // Internal - to enable these for platforms, we'll need API review
    [capitalSchemeParameter]: {
      isInternal: true,
    },
  },
  htmlEvents: [
    'onapplicationsubmitted',
    'onapplicationstepchange',
    'onapplicationinitiated',
  ],
  customHtmlElementMethods: {
    setOnApplicationSubmitted: {
      type: 'EventListenerMethod',
      event: 'onapplicationsubmitted',
      isInternal: false,
      description:
        'Sets the event listener that allows a user to pass in a callback function for when a financing application is submitted.',
    },
    setOnApplicationStepChange: {
      type: 'EventListenerMethod',
      event: 'onapplicationstepchange',
      isInternal: false,
      description: 'Sets a callback when the application step is navigated',
    },
    setPrivacyPolicyUrl: {
      type: 'AttributeSettingMethod',
      attribute: privacyPolicyUrlParameter,
      isInternal: false,
      description:
        "Customizes the URL used for the Privacy Policy link in the application component. If not set, will default to Stripe's Privacy Policy.",
    },
    setHowCapitalWorksUrl: {
      type: 'AttributeSettingMethod',
      attribute: howCapitalWorksUrlParameter,
      isInternal: false,
      description:
        "Customizes the URL used for links to documentation about the Capital program in the application component. If not set, will default to Stripe Capital's documentation.",
    },
    setDefaultFinancingOffer: {
      type: 'AttributeSettingMethod',
      attribute: defaultFinancingOfferParameter,
      isInternal: false,
      description: 'Sets the default financing offer to display',
    },
    setOnApplicationInitiated: {
      type: 'EventListenerMethod',
      event: 'onapplicationinitiated',
      isInternal: false,
      description:
        'Sets a callback that fires after the user clicks "Continue" on the offer selector screen and the application is marked as initiated',
    },
    setResumeInitiatedApplication: {
      type: 'AttributeSettingMethod',
      attribute: resumeInitiatedApplicationParameter,
      isInternal: false,
      description:
        'When set to true, causes the component to resume an already-initiated application on render, skipping the offer selector screen',
    },
    // Internal - to enable these for platforms, we'll need API review
    setCapitalScheme: {
      type: 'AttributeSettingMethod',
      attribute: capitalSchemeParameter,
      isInternal: true,
      description:
        'Sets the CapitalScheme value, used to indicate when the component is rendering in certain Stripe-owned surfaces.',
    },
  },
  releaseState: 'preview',
  sentryTeam: 'capital_core_products',
  teamOwner: 'capital-core-products',
  description:
    'Capital offer application that is available in the CapitalFinancingPromotion component but can also be standalone.',
  friendlyName: 'Capital financing application',
  docSitePath:
    '/connect/supported-embedded-components/capital-financing-application',
  syntheticTestInfo: {
    testUrl:
      'https://amp.corp.stripe.com/synthetics/us-west-2/embedded-flex-loan-individual',
  },
};
